import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomDrawerFooter from "../../../../components/CustomDrawar/customDrawarFooter";
import CustomDrawerHeader from "../../../../components/CustomDrawar/customDrawarHeader";
import SnackbarAlert from "../../../../components/SnackBar";
import { setTabActive } from "../../../../lib/slices/tabActiveSlice";
import { fetchProTeams } from "../../../../services/Team";

const ProfessionalPerServices = ({
  selectedTeam,
  selectedIds,
  setIsLoading,
  handleSelectTeam,
}) => {
  const dispatch = useDispatch();
  const tabActive = useSelector((state) => state.tabActive.tabs);
  const { user } = useSelector((state) => state.user);
  const { selectedBranch } = useSelector((state) => state.selectedBranch);
  const [proPerServices, setProPerServices] = useState([]);
  const [snackbarProps, setSnackbarProps] = useState({
    open: false,
    type: "",
    message: "",
  });
  const [selectedProfessionals, setSelectedProfessionals] = useState({});

  useEffect(() => {
    fetchProfessionalTeams();
    if (selectedTeam && selectedTeam.length > 0) {
      const previousSelections = {};
      selectedTeam.forEach((team) => {
        previousSelections[team.serviceID] = {
          _id: team.teamMemberID,
          firstname: team.teamMemberName.split(" ")[0],
          lastname: team.teamMemberName.split(" ")[1],
          image: team.teamMemberPro,
          jobtitle: team.teamMemberJobTitle,
        };
      });
      setSelectedProfessionals(previousSelections);
    }
  }, [selectedTeam]);
  const fetchProfessionalTeams = async () => {
    try {
      setIsLoading(true);
      const params = {
        branchId: selectedBranch[0],
        serviceIds: selectedIds,
      };
      const response = await fetchProTeams(params);
      setIsLoading(false);

      if (response.status === 200 || response.status === 201) {
        const teams = response.data?.servicesWithTeams || [];
        setProPerServices(teams);
      } else {
        showSnackbar(
          "error",
          response.data?.message || "Failed to fetch professionals."
        );
      }
    } catch (error) {
      setIsLoading(false);
      showSnackbar("error", "An error occurred while fetching professionals.");
    }
  };

  const showSnackbar = (type, message) => {
    setSnackbarProps({ open: true, type, message });
  };

  const handleSelection = (category, professional) => {
    setSelectedProfessionals((prev) => ({
      ...prev,
      [category._id]: professional,
    }));
  };

  const isSelected = (category, professional) => {
    const selectedPro = selectedProfessionals[category._id];
    return selectedPro && selectedPro._id === professional._id;
  };

  const handleNext = () => {
    const allSelected = proPerServices.every((category) => {
      return selectedProfessionals[category._id];
    });

    if (allSelected) {
      let newArr = [];
      proPerServices.forEach((category) => {
        const selectedPro = selectedProfessionals[category._id];
        category.teamMembers.forEach((professional) => {
          if (professional._id === selectedPro._id) {
            newArr.push({
              serviceID: category._id,
              teamMemberID: selectedPro._id,
              duration: category.duration,
              selectedFrom: "professionalPerService",
              teamMemberPro: selectedPro.image,
              teamMemberJobTitle: selectedPro.jobtitle,
              teamMemberName: `${selectedPro.firstname} ${selectedPro.lastname}`,
            });
          }
        });
      });
      handleSelectTeam(newArr);
      dispatch(setTabActive(3));
    } else {
      showSnackbar("error", "Please select a professional for each service.");
    }
  };

  const renderProfessionalCard = (category, professional) => (
    <Grid item xs={6} key={professional._id}>
      <Card
        sx={{
          textAlign: "center",
          boxShadow: 0,
          border: isSelected(category, professional)
            ? "2px solid #FF5722"
            : "1px solid #ddd",
          borderRadius: 2,
          cursor: "pointer",
          position: "relative",
        }}
        onClick={() => handleSelection(category, professional)}
      >
        {isSelected(category, professional) ? (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              color: "#FF5722",
            }}
          />
        ) : (
          <RadioButtonUncheckedIcon
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              color: "#757575",
            }}
          />
        )}
        <CardContent>
          <Avatar
            src={professional?.image}
            alt={professional?.firstname + " " + professional?.lastname}
            sx={{ width: 80, height: 80, margin: "10px auto" }}
          />
          <Typography variant="body1" sx={{ fontWeight: "medium" }}>
            {professional?.firstname} {professional?.lastname}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
      }}
    >
      <CustomDrawerHeader
        title="Select Professionals Per Services"
        onBack={() => {
          dispatch(setTabActive(1));
          handleSelectTeam([]);
        }}
      />
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          p: 2,
          pb: 10,
        }}
      >
        <Box>
          {proPerServices.map((category) => (
            <Box key={category.serviceName} sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#FF5722",
                  justifyContent: "start",
                  padding: "5px",
                  borderRadius: "5px",
                  marginBottom: "10px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#fff", fontWeight: 500 }}
                >
                  {category.serviceName}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {category.teamMembers.map((professional) =>
                  renderProfessionalCard(category, professional)
                )}
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          right: 18,
          zIndex: 1000,
          maxWidth: "390px",
          width: "390px",
          bgcolor: "background.paper",
        }}
      >
        <CustomDrawerFooter
          activeStep={tabActive}
          btnTitle="Next"
          onNext={handleNext}
        />
      </Box>
      <SnackbarAlert snackbarProps={snackbarProps} />
    </Box>
  );
};

export default ProfessionalPerServices;
